import type { NewPainEntry, PainEntry } from './model';

type ApiErrorBody = { error?: { code?: string; message?: string } };
const REQUEST_TIMEOUT_MS = 8_000;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code = 'request_failed',
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(path, {
      credentials: 'include',
      ...init,
      headers,
      signal: controller.signal,
    });
    if (!response.ok) {
      let body: ApiErrorBody = {};
      try {
        body = (await response.json()) as ApiErrorBody;
      } catch {
        // The user-facing fallback below is more useful than a parsing error.
      }
      throw new ApiError(
        body.error?.message ?? 'Cynthia Health could not complete that request.',
        response.status,
        body.error?.code,
      );
    }
    return response.json() as Promise<T>;
  } finally {
    clearTimeout(timeout);
  }
}

export async function getSession() {
  return request<{ authenticated: boolean; authRequired: boolean }>('/api/session');
}

export async function unlock(code: string) {
  return request<{ authenticated: boolean; authRequired: boolean }>('/api/session', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function listEntries() {
  return request<{ entries: PainEntry[] }>('/api/entries');
}

export async function createEntry(entry: NewPainEntry) {
  return request<{ entry: PainEntry; created: boolean }>('/api/entries', {
    method: 'POST',
    body: JSON.stringify(entry),
  });
}

export async function updateEntry(entry: PainEntry) {
  return request<{ entry: PainEntry }>(`/api/entries/${entry.id}`, {
    method: 'PUT',
    body: JSON.stringify(entry),
  });
}

export async function deleteEntry(id: string) {
  return request<{ deleted: true; id: string }>(`/api/entries/${id}`, { method: 'DELETE' });
}

export async function restoreEntry(id: string) {
  return request<{ entry: PainEntry }>(`/api/entries/${id}/restore`, { method: 'POST', body: '{}' });
}

export async function completeIntervention(id: string, afterPain: number) {
  return request<{ entry: PainEntry }>(`/api/interventions/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify({ afterPain }),
  });
}

export async function addWeather(id: string, latitude?: number, longitude?: number) {
  return request<{ entry: PainEntry }>(`/api/entries/${id}/weather`, {
    method: 'POST',
    body: JSON.stringify(latitude === undefined || longitude === undefined ? {} : { latitude, longitude }),
  });
}

export async function markWeatherUnavailable(id: string) {
  return request<{ entry: PainEntry }>(`/api/entries/${id}/weather-unavailable`, {
    method: 'POST',
    body: '{}',
  });
}
